import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Skeleton,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@bloomcode/ui';
import {
  useListPostsQuery,
  useListCmsCategoriesQuery,
  type ListPostsQuery,
  type ListCmsCategoriesQuery,
  type PostStatus,
} from '@bloomcode/graphql';
import { formatDate } from '@bloomcode/utils';
import { useCookieSession } from '@bloomcode/auth';

type PostItem = ListPostsQuery['listPosts']['data'][number];
type CategoryItem = ListCmsCategoriesQuery['listCategories']['data'][number];

export default function CategoryPage() {
  const { category: categorySlug } = useParams<{ category: string }>();
  const { session } = useCookieSession();
  const [page, setPage] = useState(1);
  const limit = 12;

  const { data: categoriesData, loading: categoriesLoading } = useListCmsCategoriesQuery({
    variables: { pagination: { page: 1, limit: 100 } },
    fetchPolicy: 'cache-and-network',
    skip: !session,
  });

  const categories: CategoryItem[] = categoriesData?.listCategories?.data ?? [];
  const decodedSlug = decodeURIComponent(categorySlug || '');
  const currentCategory = categories.find(
    (c) => c.slug.toLowerCase() === decodedSlug.toLowerCase()
  );

  const {
    data: postsData,
    loading: postsLoading,
    error: postsError,
    refetch,
  } = useListPostsQuery({
    variables: {
      filters: {
        status: ['PUBLISHED' as PostStatus],
        ...(currentCategory ? { category_id: currentCategory.id } : {}),
      },
      pagination: { page, limit },
    },
    fetchPolicy: 'cache-and-network',
    skip: !session || !currentCategory,
  });

  const posts: PostItem[] = postsData?.listPosts?.data ?? [];
  const pagination = postsData?.listPosts?.pagination;
  const isLoading = categoriesLoading || postsLoading;

  if (isLoading && !posts.length) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Skeleton className="h-8 w-1/4 mb-8" />
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-3/4" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-4 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (postsError) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <p className="text-destructive mb-4">Failed to load content: {postsError.message}</p>
        <button onClick={() => refetch()} className="text-primary underline">
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{currentCategory?.name || decodedSlug} | Bloomcode CMS</title>
        <meta
          name="description"
          content={currentCategory?.description || `Browse ${decodedSlug} content`}
        />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/content">Content</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{currentCategory?.name || decodedSlug}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-4">{currentCategory?.name || decodedSlug}</h1>
          {currentCategory?.description && (
            <p className="text-muted-foreground mb-4">{currentCategory.description}</p>
          )}
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Link key={cat.id} to={`/category/${encodeURIComponent(cat.slug)}`}>
                <Badge
                  variant={
                    cat.slug.toLowerCase() === decodedSlug.toLowerCase() ? 'default' : 'outline'
                  }
                >
                  {cat.name}
                </Badge>
              </Link>
            ))}
          </div>
        </div>

        {posts.length > 0 ? (
          <>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {posts.map((post) => (
                <Link key={post.id} to={`/content/${post.slug}`}>
                  <Card className="h-full hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        {post.categories?.[0] && (
                          <Badge variant="secondary">{post.categories[0].name}</Badge>
                        )}
                        <span className="text-sm text-muted-foreground">
                          {formatDate(post.published_at || post.created_at)}
                        </span>
                      </div>
                      <CardTitle className="line-clamp-2">{post.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {post.excerpt && (
                        <p className="text-muted-foreground line-clamp-3">{post.excerpt}</p>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            {pagination && pagination.pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={!pagination.hasPrev}
                  className="px-4 py-2 text-sm border border-border rounded-md disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-muted-foreground">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!pagination.hasNext}
                  className="px-4 py-2 text-sm border border-border rounded-md disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No content found in this category.</p>
          </div>
        )}
      </div>
    </>
  );
}
